import { apiClient } from './client';
import { useAuth, readRefreshToken, type SessionUser } from '@/store/auth';
import { adoptSession, endSession, refreshSession } from '@/lib/session';

export type AuthSource = 'CRYSTAL' | 'MAFA_BO' | 'CALL_CENTER' | 'LOCAL';

export type AuthError =
  | 'invalidCredentials'
  | 'accountDisabled'
  | 'sourceUnreachable'
  | 'sourceMissing'
  | 'missingFields'
  | 'unknown';

type LoginBody = {
  accessToken: string;
  refreshToken: string;
  accountId: number;
  email: string;
  displayName: string;
  source: string;
  roles: string[];
  expiresAt: string;
  sessionId?: number;
  idleTimeoutSec?: number;
  sessionExpiresAt?: string;
};

export async function listAuthSources(): Promise<AuthSource[]> {
  const { data, error } = await apiClient.GET('/v1/auth/sources');
  if (error || !data) return [];
  // The OpenAPI types describe sources as string[]; we cast back to the
  // small union the UI uses to drive the selector.
  return (data.sources ?? []) as AuthSource[];
}

export type LoginInput = {
  sourceSystem: AuthSource;
  username: string;
  password: string;
};

export async function login(input: LoginInput): Promise<{ ok: true } | { ok: false; reason: AuthError }> {
  if (!input.sourceSystem) return { ok: false, reason: 'sourceMissing' };
  if (!input.username || !input.password) return { ok: false, reason: 'missingFields' };

  const { data, error, response } = await apiClient.POST('/v1/auth/login', {
    body: input,
  });
  if (error || !data) {
    return { ok: false, reason: mapAuthHttpError(response.status) };
  }
  adoptSession(data as LoginBody);
  return { ok: true };
}

export async function fetchMe(): Promise<SessionUser | null> {
  const { data, error } = await apiClient.GET('/v1/auth/me');
  if (error || !data) return null;
  const body = data as Omit<LoginBody, 'accessToken' | 'refreshToken'>;
  const user: SessionUser = {
    accountId: body.accountId,
    email: body.email,
    displayName: body.displayName,
    source: body.source,
    roles: body.roles,
    expiresAt: body.expiresAt,
  };
  useAuth.getState().setUser(user);
  return user;
}

/**
 * Rejoue une session au démarrage de l'application, à partir du seul jeton de
 * renouvellement survivant au rechargement.
 */
export async function restoreSession(): Promise<boolean> {
  if (!readRefreshToken()) return false;
  return (await refreshSession()) !== null;
}

/** Ferme la session, ici et côté serveur. */
export function logout() {
  endSession('user');
}

export function mapAuthHttpError(status: number): AuthError {
  if (status === 401) return 'invalidCredentials';
  if (status === 403) return 'accountDisabled';
  if (status === 502 || status === 503) return 'sourceUnreachable';
  return 'unknown';
}

/**
 * Ferme TOUTES les connexions du compte, y compris celle-ci.
 *
 * Le bouton existait déjà sous ce nom mais ne faisait qu'oublier le jeton dans
 * ce navigateur : les autres appareils restaient connectés. C'est précisément
 * ce qu'on veut quand on soupçonne qu'un poste n'est plus sous contrôle.
 */
export async function logoutEverywhere(): Promise<boolean> {
  const { error } = await apiClient.POST('/v1/auth/logout-all', {});
  endSession('user');
  return !error;
}

export type ActiveSession = {
  id: number;
  current: boolean;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  ip?: string;
  userAgent?: string;
};

/** Les connexions ouvertes du compte courant, la plus active en tête. */
export async function listSessions(): Promise<ActiveSession[]> {
  const { data, error } = await apiClient.GET('/v1/auth/sessions');
  if (error || !data) return [];
  return (data.sessions ?? []) as ActiveSession[];
}
